import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useI18n } from '@/hooks/useI18n';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { RefreshCw, AlertCircle, ChevronRight } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { formatAmount } from '@/utils/formatters';
import CostReportInvoicesModal from '@/components/CostReportInvoicesModal';

interface CostCenter {
    id: string;
    name: string;
}

interface CategoryCostCenter {
    amount: number | string;
    invoice_count: number;
}

interface Category {
    category_id?: string;
    category_name: string;
    parent_category_name?: string;
    parent_category_id?: string;
    category_internal_id?: string;
    cost_centers: Record<string, CategoryCostCenter>;
    row_total: number;
}

interface ReportData {
    year: number;
    month: number;
    month_name: string;
    base_currency: string;
    report_generated_at: string;
    grand_total: number;
    cost_centers: CostCenter[];
    categories: Category[];
}

interface YearOption {
    value: number;
    label: string;
}

interface MonthOption {
    value: number;
    label: string;
}

export default function CostReportsPage() {
    const { t } = useI18n();
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Current date for default values
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth() + 1);

    // Modal state
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalData, setModalData] = useState<{
        categoryName: string;
        costCenterName: string;
        categoryId: string;
        costCenterId: string;
    } | null>(null);

    // Generate year options (current year and previous 5 years)
    const yearOptions: YearOption[] = [];
    for (let i = 0; i < 6; i++) {
        const year = currentDate.getFullYear() - i;
        yearOptions.push({ value: year, label: year.toString() });
    }

    // Helper function to sort categories by internal_id (same as categories page)
    const sortCategories = (a: Category, b: Category) => {
        const aHas = !!a.category_internal_id && a.category_internal_id.trim() !== '';
        const bHas = !!b.category_internal_id && b.category_internal_id.trim() !== '';
        if (aHas && bHas) return a.category_internal_id!.localeCompare(b.category_internal_id!);
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        return a.category_name.localeCompare(b.category_name);
    };

    const orderedCategories = React.useMemo(() => {
        if (!reportData) return [] as Array<{ category: Category; level: number }>;

        const withId = reportData.categories.filter((category) => !!category.category_id);
        const withoutId = reportData.categories.filter((category) => !category.category_id);
        const categoryById = new Map(withId.map((category) => [category.category_id as string, category]));
        const childrenMap = new Map<string, Category[]>();
        const roots: Category[] = [];

        withId.forEach((category) => {
            const parentId = category.parent_category_id;
            if (parentId && categoryById.has(parentId)) {
                const siblings = childrenMap.get(parentId) || [];
                siblings.push(category);
                childrenMap.set(parentId, siblings);
            } else {
                roots.push(category);
            }
        });

        const sortInPlace = (items: Category[]) => items.sort(sortCategories);
        sortInPlace(roots);
        childrenMap.forEach((siblings) => sortInPlace(siblings));
        sortInPlace(withoutId);

        const ordered: Array<{ category: Category; level: number }> = [];
        const dfs = (category: Category, level: number) => {
            ordered.push({ category, level });
            const children = childrenMap.get(category.category_id as string) || [];
            children.forEach((child) => dfs(child, level + 1));
        };
        roots.forEach((root) => dfs(root, 0));
        withoutId.forEach((category) => ordered.push({ category, level: 0 }));

        return ordered;
    }, [reportData]);

    // Calculate totals by cost center (sum all category rows, including parent categories' own amounts)
    const costCenterTotals = reportData ? reportData.cost_centers.reduce((acc, costCenter) => {
        const total = reportData.categories.reduce((sum, category) => {
            const cellData = category.cost_centers[costCenter.id.toString()];
            const amount = parseFloat(String(cellData?.amount || 0));
            return sum + amount;
        }, 0);
        acc[costCenter.id.toString()] = total; // Use string key to match table access pattern
        return acc;
    }, {} as Record<string, number>) : {};

    // Use backend-provided grand total to avoid any UI double-counting
    const calculatedGrandTotal = reportData?.grand_total ?? Object.values(costCenterTotals).reduce((sum, total) => sum + total, 0);

    // Month options
    const monthOptions: MonthOption[] = [
        { value: 1, label: t('dataImport.monthNames.1', 'common') },
        { value: 2, label: t('dataImport.monthNames.2', 'common') },
        { value: 3, label: t('dataImport.monthNames.3', 'common') },
        { value: 4, label: t('dataImport.monthNames.4', 'common') },
        { value: 5, label: t('dataImport.monthNames.5', 'common') },
        { value: 6, label: t('dataImport.monthNames.6', 'common') },
        { value: 7, label: t('dataImport.monthNames.7', 'common') },
        { value: 8, label: t('dataImport.monthNames.8', 'common') },
        { value: 9, label: t('dataImport.monthNames.9', 'common') },
        { value: 10, label: t('dataImport.monthNames.10', 'common') },
        { value: 11, label: t('dataImport.monthNames.11', 'common') },
        { value: 12, label: t('dataImport.monthNames.12', 'common') }
    ];

    const fetchCostReport = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch(`/api/cost-reports/monthly?year=${selectedYear}&month=${selectedMonth}`);

            if (!response.ok) {
                throw new Error(`Failed to fetch cost report: ${response.status}`);
            }

            const data = await response.json();
            setReportData(data);
        } catch (err) {
            console.error('Error fetching cost report:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Load report on component mount and when year/month changes
    useEffect(() => {
        fetchCostReport();
    }, [selectedYear, selectedMonth]);

    const handleGenerateReport = () => {
        fetchCostReport();
    };

    const handleCellClick = (
        categoryId: string,
        categoryName: string,
        costCenterId: string,
        costCenterName: string,
        amount: number
    ) => {
        if (amount > 0) {
            setModalData({
                categoryId,
                categoryName,
                costCenterId,
                costCenterName
            });
            setModalOpen(true);
        }
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('costReports.title', 'finance')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('costReports.description', 'finance')}
                        </p>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-4 p-6 rounded-lg border bg-card">
                    <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">{t('costReports.year', 'finance')}:</Label>
                        <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                            <SelectTrigger className="w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map(option => (
                                    <SelectItem key={option.value} value={option.value.toString()}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">{t('costReports.month', 'finance')}:</Label>
                        <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                            <SelectTrigger className="w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {monthOptions.map(option => (
                                    <SelectItem key={option.value} value={option.value.toString()}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button onClick={handleGenerateReport} disabled={loading}>
                        {loading ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                {t('costReports.generating', 'finance')}
                            </>
                        ) : (
                            t('costReports.generateReport', 'finance')
                        )}
                    </Button>
                </div>

                {/* Error Display */}
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{t('status.error', 'common')}: {error}</AlertDescription>
                    </Alert>
                )}

                {/* Report Display */}
                {reportData && (
                    <div className="space-y-4">
                        {/* Cost Report Table */}
                        <div className="rounded-lg border bg-card overflow-hidden max-w-[calc(100vw-16rem-3rem)] group-has-[[data-collapsible=icon]]/sidebar-wrapper:max-w-[calc(100vw-3rem-3rem)]">
                            <div className="relative">
                                <div className="overflow-x-auto">
                                    <Table className="w-auto min-w-full">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="sticky left-0 bg-muted z-20 whitespace-nowrap px-2 py-2 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                                                    {t('costReports.category', 'finance')}
                                                </TableHead>
                                                {reportData.cost_centers.map(costCenter => (
                                                    <TableHead key={costCenter.id} className="text-center min-w-[80px] max-w-[100px] px-1 py-3">
                                                        <div className="space-y-1">
                                                            <div className="font-medium text-xs break-words hyphens-auto leading-tight" title={costCenter.name}>
                                                                {costCenter.name}
                                                            </div>
                                                        </div>
                                                    </TableHead>
                                                ))}
                                                <TableHead className="text-center min-w-[120px] bg-muted px-2 py-2">
                                                    {t('costReports.total', 'finance')}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {orderedCategories.map(({ category, level }, index) => (
                                                <TableRow key={category.category_id || `${category.category_name}-${index}`}>
                                                    <TableCell className="sticky left-0 bg-background z-20 font-medium px-1 py-2 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)] whitespace-nowrap">
                                                        <div style={{ paddingLeft: `${level * 1.25}rem` }} className="flex items-center gap-1">
                                                            {level > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                                                            <span>{category.category_name}</span>
                                                        </div>
                                                    </TableCell>
                                                    {reportData.cost_centers.map(costCenter => {
                                                        const cellData = category.cost_centers[costCenter.id.toString()];
                                                        const amount = parseFloat(String(cellData?.amount || 0));

                                                        return (
                                                            <TableCell key={costCenter.id} className="text-right px-1 py-2">
                                                                {amount > 0 ? (
                                                                    <div
                                                                        className="space-y-1 cursor-pointer hover:bg-muted/50 rounded px-1 py-1 transition-colors"
                                                                        onClick={() => handleCellClick(
                                                                            category.category_id || '',
                                                                            category.category_name,
                                                                            costCenter.id,
                                                                            costCenter.name,
                                                                            amount
                                                                        )}
                                                                        title={t('costReports.clickToViewInvoices', 'finance')}
                                                                    >
                                                                        <div className="font-medium text-blue-600 hover:text-blue-800">
                                                                            {formatAmount(amount, { currencyCode: reportData.base_currency })}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted-foreground">-</span>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell className="text-right font-medium bg-muted px-1 py-2">
                                                        {category.row_total > 0 ? formatAmount(category.row_total, { currencyCode: reportData.base_currency }) : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {/* Grand Total Row */}
                                            <TableRow className="bg-primary/10 border-t-2 border-primary/20 hover:bg-primary/10">
                                                <TableCell className="sticky left-0 bg-primary/10 z-20 font-bold text-foreground py-2 px-2 border-r border-border shadow-[2px_0_4px_rgba(0,0,0,0.1)] whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <span className="text-sm uppercase tracking-wide">
                                                            {t('costReports.grandTotal', 'finance')}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                {reportData.cost_centers.map(costCenter => {
                                                    const total = costCenterTotals[costCenter.id.toString()] || 0;

                                                    return (
                                                        <TableCell key={costCenter.id} className="text-right font-bold bg-primary/5 px-1 py-2">
                                                            {total > 0 ? (
                                                                <div className="text-foreground">
                                                                    {formatAmount(total, { currencyCode: reportData.base_currency })}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted-foreground">-</span>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell className="text-right font-bold bg-primary/10 text-foreground px-1 py-2">
                                                    {formatAmount(calculatedGrandTotal, { currencyCode: reportData.base_currency })}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>

                            {/* Summary */}
                            {reportData.categories.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    {t('messages.noData', 'common')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && !reportData && (
                    <div className="text-center py-8">
                        <div className="flex items-center justify-center space-x-2">
                            <Skeleton className="h-4 w-4 rounded-full" />
                            <Skeleton className="h-4 w-[150px]" />
                        </div>
                        <p className="mt-2 text-muted-foreground">{t('costReports.generatingReport', 'finance')}</p>
                    </div>
                )}

                {/* Invoices Modal */}
                {modalData && (
                    <CostReportInvoicesModal
                        isOpen={modalOpen}
                        onClose={() => setModalOpen(false)}
                        categoryName={modalData.categoryName}
                        costCenterName={modalData.costCenterName}
                        categoryId={modalData.categoryId}
                        costCenterId={modalData.costCenterId}
                        year={selectedYear}
                        month={selectedMonth}
                        baseCurrency={reportData?.base_currency || 'USD'}
                    />
                )}
            </div>
        </Layout >
    );
}

export const Route = createFileRoute('/cost-reports')({
  component: CostReportsPage,
})
